import { Component, Input, OnInit } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { NeqTreeFlatNode } from './neq-tree-flat-node.model';
import { NeqTreeNode } from './neq-tree-node.model';
import { Observable } from 'rxjs';
import { NeqTreeDatabase } from './neq-tree-database.model';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';

@Component({
  selector: 'neq-tree',
  templateUrl: './neq-tree.component.html'
})
export class NeqTreeTreeComponent implements OnInit {

  @Input()
  database: NeqTreeDatabase;

  nodeMap = new Map<string, NeqTreeFlatNode>();
  dataSource: MatTreeFlatDataSource<NeqTreeNode, NeqTreeFlatNode>;
  treeControl: FlatTreeControl<NeqTreeFlatNode>;
  treeFlattener: MatTreeFlattener<NeqTreeNode, NeqTreeFlatNode>;

  /* Drag and drop */
  dragNode: any;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: any;
  dragNodeExpandOverTime: number;
  dragNodeExpandOverArea: string;

  constructor() {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
      this.isExpandable, this.getChildren);

    this.treeControl = new FlatTreeControl<NeqTreeFlatNode>(this.getLevel, this.isExpandable);

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngOnInit(): void {

    this.database.dataChange.subscribe(data => {
      this.dataSource.data = data;
    });

    this.database.initialize();
  }

  transformer = (node: NeqTreeNode, level: number) => {
    const existingNode = this.nodeMap.get(node.identifier);

    if (existingNode) {
      return existingNode;
    }

    const newNode =
      new NeqTreeFlatNode(node.label, node.type, node.code, level, node.hasChildren);
    this.nodeMap.set(node.identifier, newNode);
    return newNode;
  }

  getChildren = (node: NeqTreeNode): Observable<NeqTreeNode[]> => node.childrenChange;

  getLevel = (node: NeqTreeFlatNode) => node.level;

  isExpandable = (node: NeqTreeFlatNode) => node.expandable;

  hasChild = (_: number, _nodeData: NeqTreeFlatNode) => _nodeData.expandable;

  /** Load more nodes from data source */
  loadChildren(node: NeqTreeFlatNode) {
    this.database.loadMore(node, true);
  }

  handleDragStart(event, node) {
    // Required by Firefox (https://stackoverflow.com/questions/19055264/why-doesnt-html5-drag-and-drop-work-in-firefox)
    if (event.dataTransfer) {
      event.dataTransfer.setData('text', 'bar'); // must be 'text' on IE11
    }
    if (event.dataTransfer.setDragImage) { // IE11
      event.dataTransfer.setDragImage(event.srcElement, 0, 0);
    }

    this.dragNode = node;
    this.treeControl.collapse(node);
  }

  handleDragOver(event, node: NeqTreeFlatNode) {
    event.preventDefault();

    // Handle node expand
    if (node === this.dragNodeExpandOverNode) {
      if (this.dragNode !== node &&
        !this.treeControl.isExpanded(node) &&
        this.database.canBeChildOf(this.dragNode, this.database.getNode(node.identifier))) {
        if ((new Date().getTime() - this.dragNodeExpandOverTime) > this.dragNodeExpandOverWaitTimeMs) {
          this.database.loadMore(node, true);
          this.treeControl.expand(node);
        }
      }
    } else {
      this.dragNodeExpandOverNode = node;
      this.dragNodeExpandOverTime = new Date().getTime();
    }

    // Handle drag area
    const percentageY = event.offsetY / event.target.clientHeight;
    if (percentageY < 0.25) {
      this.dragNodeExpandOverArea = 'above';
    } else if (percentageY > 0.75) {
      this.dragNodeExpandOverArea = 'below';
    } else {
      this.dragNodeExpandOverArea = 'center';
    }
  }

  handleDrop(event, node) {
    event.preventDefault();
    if (node !== this.dragNode) {
      const dragNode = this.database.getNode(this.dragNode.identifier);
      if (this.dragNodeExpandOverArea === 'above') {
        this.database.insertItemAbove(this.database.getNode(node.identifier), dragNode);
      } else if (this.dragNodeExpandOverArea === 'below') {
        this.database.insertItemBelow(this.database.getNode(node.identifier), dragNode);
      } else {
        this.database.insertItem(this.database.getNode(node.identifier), dragNode);
      }
    }
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  handleDragEnd() {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

}
